'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { signOut, useSession } from 'next-auth/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import { schemaPrimeiroAcesso } from '~/lib/validations';

import { StyledLoginForm } from '../../Login/components/elements';
import { novaSenha } from '../hooks/useNewPassword';

type PrimeiroAcessoFormData = {
  password: string;
  confirmPassword: string;
};

export function PrimeiroAcessoForm() {
  const router = useRouter();
  const { data: session } = useSession();
  const email = session?.user?.email ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PrimeiroAcessoFormData>({
    resolver: zodResolver(schemaPrimeiroAcesso),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [apiError, setApiError] = useState('');

  const onSubmit = async ({ password }: PrimeiroAcessoFormData) => {
    if (!email) {
      setApiError('Sessão expirada. Faça login novamente.');
      router.push('/login');
      return;
    }
    setApiError('');
    try {
      await novaSenha(email, password);
      await signOut({ callbackUrl: '/login' });
    } catch {
      setApiError('Não foi possível salvar a senha. Tente novamente.');
    }
  };

  return (
    <StyledLoginForm>
      {apiError && (
        <Typography sx={{ color: theme => theme.palette.error.light }}>
          {apiError}
        </Typography>
      )}

      <TextField
        disabled={isSubmitting}
        {...register('password')}
        error={!!errors.password}
        helperText={errors.password?.message?.toString() || ''}
        id="password"
        type={showPassword ? 'text' : 'password'}
        label="Nova senha"
        variant="filled"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <VisibilityOutlined />
                ) : (
                  <VisibilityOffOutlined />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        disabled={isSubmitting}
        {...register('confirmPassword')}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message?.toString() || ''}
        id="confirmPassword"
        type={showConfirm ? 'text' : 'password'}
        label="Confirmar senha"
        variant="filled"
        sx={{ marginBottom: theme => theme.spacing(4) }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Toggle confirm password visibility"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? (
                  <VisibilityOutlined />
                ) : (
                  <VisibilityOffOutlined />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        disabled={isSubmitting}
        type="submit"
        color="primary"
        variant="contained"
        sx={{ textTransform: 'none' }}
        onClick={handleSubmit(onSubmit)}
        size="large"
      >
        Salvar senha
      </Button>
    </StyledLoginForm>
  );
}
